import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import {
  FloppyDisk,
  CircleNotch,
  Envelope,
  ArrowCounterClockwise,
  Pencil,
} from "@phosphor-icons/react";

import type { EmailTemplate } from "@/types";
import { EMAIL_TEMPLATE_SUBJECT_MAX, EMAIL_TEMPLATE_BODY_MAX } from "@/schemas/email-template";

export function EmailTemplatesSection() {
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [confirmingReset, setConfirmingReset] = useState(false);

  const { data: templates, isLoading } = useQuery({
    queryKey: ["admin", "email-templates"],
    queryFn: async () => {
      const res = await fetch("/api/email-templates");
      await throwIfNotOk(res, "Greška pri učitavanju email šablona");
      return res.json() as Promise<EmailTemplate[]>;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ key, subject, body }: { key: string; subject: string; body: string }) => {
      const res = await fetch(`/api/email-templates/${key}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject, body }),
      });
      await throwIfNotOk(res, "Greška pri spremanju šablona");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "email-templates"] });
      toast.success("Šablon uspješno spremljen.");
      setEditingTemplate(null);
    },
  });

  const resetMutation = useMutation({
    mutationFn: async (key: string) => {
      const res = await fetch(`/api/email-templates/${key}`, {
        method: "DELETE",
      });
      await throwIfNotOk(res, "Greška pri resetovanju šablona");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "email-templates"] });
      toast.success("Šablon vraćen na zadanu vrijednost.");
      setConfirmingReset(false);
      setEditingTemplate(null);
    },
  });

  const openEdit = (tpl: EmailTemplate) => {
    setEditingTemplate(tpl);
    setEditSubject(tpl.subject);
    setEditBody(tpl.body);
  };

  const handleSave = () => {
    if (!editingTemplate) return;
    saveMutation.mutate({ key: editingTemplate.key, subject: editSubject, body: editBody });
  };

  const handleReset = () => {
    if (!editingTemplate) return;
    setConfirmingReset(true);
  };

  return (
    <>
      <div className="mt-10 border-t pt-8">
        <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl" style={{ fontFamily: "var(--font-heading)" }}>
          Email šabloni
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Prilagodite sadržaj email poruka koje platforma šalje korisnicima.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-xl border bg-card p-5">
              <div className="flex items-start gap-4">
                <div className="size-10 animate-pulse rounded-xl bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-48 animate-pulse rounded bg-muted" />
                  <div className="h-3.5 w-64 animate-pulse rounded bg-muted/60" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {templates?.map((tpl) => (
            <div key={tpl.key} className="rounded-xl border bg-card p-4 shadow-sm transition-shadow hover:shadow-md sm:p-5">
              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/8">
                  <Envelope className="size-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground">{tpl.description}</h3>
                    {tpl.isCustomized && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">Prilagođeno</span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground truncate">Predmet: {tpl.subject}</p>
                </div>
                <button onClick={() => openEdit(tpl)} className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                  <Pencil className="size-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editingTemplate} onClose={() => setEditingTemplate(null)}>
        <DialogHeader onClose={() => setEditingTemplate(null)}>
          <AdminDialogHeading
            icon={Envelope}
            title="Uredi email šablon"
            description="Ažurirajte predmet i sadržaj sistemskog emaila."
          />
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-subject">Predmet</Label>
              <Input
                id="tpl-subject"
                value={editSubject}
                maxLength={EMAIL_TEMPLATE_SUBJECT_MAX}
                onChange={(e) => setEditSubject(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-body">Sadržaj</Label>
              <Textarea
                id="tpl-body"
                rows={6}
                value={editBody}
                maxLength={EMAIL_TEMPLATE_BODY_MAX}
                onChange={(e) => setEditBody(e.target.value)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Koristite {"{{name}}"}, {"{{reason}}"}, {"{{title}}"}, {"{{author}}"} kao zamjenske oznake.
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          {editingTemplate?.isCustomized && (
            <Button
              type="button"
              variant="outline"
              disabled={resetMutation.isPending}
              onClick={handleReset}
            >
              <ArrowCounterClockwise className="size-4" />
              {resetMutation.isPending ? "Vraćanje..." : "Vrati na zadano"}
            </Button>
          )}
          <Button
            type="button"
            disabled={saveMutation.isPending || !editSubject.trim() || !editBody.trim()}
            onClick={handleSave}
          >
            {saveMutation.isPending ? (
              <CircleNotch className="size-4 animate-spin" />
            ) : (
              <FloppyDisk className="size-4" />
            )}
            {saveMutation.isPending ? "Spremanje..." : "Spremi"}
          </Button>
        </DialogFooter>
      </Dialog>

      <ConfirmDialog
        open={confirmingReset}
        title="Vrati na zadano"
        description="Da li ste sigurni da želite vratiti šablon na zadanu vrijednost?"
        confirmLabel="Vrati na zadano"
        tone="destructive"
        isPending={resetMutation.isPending}
        onClose={() => setConfirmingReset(false)}
        onConfirm={() => editingTemplate && resetMutation.mutate(editingTemplate.key)}
      />
    </>
  );
}
